import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import Auth from '@aws-amplify/auth';
import { Hub, HubCapsule } from '@aws-amplify/core';
import { Authenticator } from 'aws-amplify-react';
import { isMobile } from 'react-device-detect';
import { Provider } from 'react-redux';
import { store } from 'core';

import { Preloader } from 'components/preloader';

import { Modules } from 'core/types';
import { IUserInfo } from 'modules/auth/types';

import { getGoogleFonts } from 'core/actions';
import { config } from 'core/services/Auth';
import { logout } from 'core/utils/logout';
import { getUserInfo } from 'modules/auth/actions';
import {
  ConfirmationWasSent,
  ConfirmSignIn,
  ForgotPassword,
  MobilePreview,
  SignIn,
  SignUp,
} from 'modules/auth/containers';

Auth.configure(config);

const BaseLayout = lazy(() => import('components/layout/base-layout'));

const handleUserSubscription = ({ payload }: HubCapsule) => {
  if (payload.event === 'updateUserAttributes') {
    store.dispatch(getUserInfo());
  }
};

const AppComponent = ({ routes }: Partial<Modules>) => {
  const [user, setUser] = useState<IUserInfo | null>(null);

  const fetchUserInfo = useCallback(async () => {
    const resultAction = await store.dispatch(getUserInfo());

    if (getUserInfo.rejected.match(resultAction)) {
      logout();
    }

    if (getUserInfo.fulfilled.match(resultAction)) {
      if (!resultAction?.payload?.username) {
        return logout();
      }

      setUser(resultAction?.payload);
    }
  }, []);

  useEffect(() => {
    fetchUserInfo();
    store.dispatch(getGoogleFonts());
  }, [fetchUserInfo]);

  useEffect(() => {
    Hub.listen('auth', handleUserSubscription);
    return () => {
      Hub.remove('auth', handleUserSubscription);
    };
  }, []);

  if (!user) {
    return <Preloader />;
  }

  return (
    <Provider store={store}>
      <Suspense fallback={<Preloader />}>
        <BaseLayout routes={routes} />
      </Suspense>
    </Provider>
  );
};

export const App = (props: Modules) => {
  const urlParams = new URLSearchParams(window.location.search);
  const webViewType = urlParams.get('webview');
  const authPage = urlParams.get('authPage');
  const skipAppPlaceholder = urlParams.get('skipapp');

  const isSkipPreview = webViewType === 'mobile' && authPage === 'signin';

  const [authState, setAuthState] = useState<string | null>();

  const handleAuthStateChange = (state: string) => {
    setAuthState(state);
  };

  if (authState === 'signedIn') {
    return <AppComponent {...props} />;
  }

  return (
    <Authenticator
      hideDefault={true}
      authState={isMobile && !isSkipPreview && !skipAppPlaceholder ? 'mobilePreview' : 'signIn'}
      onStateChange={handleAuthStateChange}>
      <MobilePreview key="mobilePreview" />
      <SignIn key="signIn" />
      <SignUp key="signUp" />
      <ConfirmationWasSent key="confirmSignUp" />
      <ForgotPassword key="forgotPassword" />
      <ConfirmSignIn key="confirmSignIn" />
    </Authenticator>
  );
};
